import { useCallback, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { usePermissions } from "../contexts/PermissionContext";
import {
	useMessages as useMessagesQuery,
	useUnreadMessagesCount,
	useMarkMessageAsRead,
	useMarkMultipleMessagesAsRead,
	useCreateMessage,
	useUpdateMessage,
	useDeleteMessage,
	messageKeys
} from "./useMessagesQueries";
import { useMessageUrgency } from "./useMessageUrgency";
import {
	MessageFilter,
	CreateMessagePayload,
	UpdateMessagePayload,
} from "../types/messages";
import { UserRole } from "../types/permissions";
import { messagesService } from "../services/messagesService";

interface UseMessagesOptions {
	initialFilters?: MessageFilter;
	initialUseAdminRoute?: boolean;
}

/**
 * Hook principal para gerenciar mensagens
 * Facade para React Query hooks
 */
export function useMessages(options: UseMessagesOptions = {}) {
	const { role, impersonatedRole } = usePermissions();
	const { sortByUrgency } = useMessageUrgency();
	const queryClient = useQueryClient();
	const initialFilters = options.initialFilters || {};
	const initialUseAdminRoute = options.initialUseAdminRoute || false;
	const [activeFilters, setActiveFilters] = useState<MessageFilter>(initialFilters);
	const [activeUseAdminRoute, setActiveUseAdminRoute] = useState(initialUseAdminRoute);
	const activeFiltersRef = useRef<MessageFilter>(initialFilters);
	const activeUseAdminRouteRef = useRef(initialUseAdminRoute);
	const refreshPromiseRef = useRef<Promise<void> | null>(null);

	// Hooks do React Query
	const {
		data: messagesData,
		isLoading: isMessagesLoading,
		error: messagesError
	} = useMessagesQuery(activeFilters, true, activeUseAdminRoute);

	const {
		data: unreadCountData,
		isLoading: isCountLoading,
		refetch: refetchCount
	} = useUnreadMessagesCount(true);

	// Mutations
	const markAsReadMutation = useMarkMessageAsRead();
	const markMultipleAsReadMutation = useMarkMultipleMessagesAsRead();
	const createMessageMutation = useCreateMessage();
	const updateMessageMutation = useUpdateMessage();
	const deleteMessageMutation = useDeleteMessage();

	/**
	 * Determina o role efetivo (considera simulaÃ§Ã£o)
	 */
	const effectiveRole: UserRole = impersonatedRole || role
	const isAdmin = effectiveRole === "admin";

	// Preparar dados derivados
	const messages = messagesData?.data?.messages
		? sortByUrgency(messagesData.data.messages)
		: [];

	/**
	 * Atualiza a lista de mensagens e contagem
	 */
	const refresh = useCallback(
		async (filters?: MessageFilter, useAdminRoute?: boolean) => {
			if (refreshPromiseRef.current) {
				return refreshPromiseRef.current;
			}

			const nextFilters = filters ?? activeFiltersRef.current;
			const nextUseAdminRoute = useAdminRoute ?? activeUseAdminRouteRef.current;

			activeFiltersRef.current = nextFilters;
			activeUseAdminRouteRef.current = nextUseAdminRoute;

			setActiveFilters((prev) => {
				const prevJson = JSON.stringify(prev || {});
				const nextJson = JSON.stringify(nextFilters || {});
				return prevJson === nextJson ? prev : nextFilters;
			});
			setActiveUseAdminRoute((prev) =>
				prev === nextUseAdminRoute ? prev : nextUseAdminRoute
			);

			const refreshPromise = Promise.all([
				queryClient.fetchQuery({
					queryKey: messageKeys.list(nextFilters, nextUseAdminRoute),
					queryFn: () => messagesService.getAll(nextFilters, nextUseAdminRoute),
				}),
				refetchCount(),
			]).then(() => undefined)
				.finally(() => {
					refreshPromiseRef.current = null;
				});

			refreshPromiseRef.current = refreshPromise;
			await refreshPromise;
		},
		[queryClient, refetchCount]
	);

	/**
	 * Marca uma mensagem como lida
	 */
	const markAsRead = useCallback(
		async (messageId: number) => {
			const result = await markAsReadMutation.mutateAsync(messageId);
			return result;
		},
		[markAsReadMutation]
	);

	/**
	 * Marca mÃºltiplas mensagens como lidas
	 */
	const markMultipleAsRead = useCallback(
		async (messageIds: number[]) => {
			const result = await markMultipleAsReadMutation.mutateAsync(messageIds);
			return result;
		},
		[markMultipleAsReadMutation]
	);

	/**
	 * Cria uma nova mensagem (admin)
	 */
	const createMessage = useCallback(
		async (data: CreateMessagePayload) => {
			if (!isAdmin) throw new Error("Apenas administradores");
			const result = await createMessageMutation.mutateAsync(data);
			return result.data;
		},
		[createMessageMutation, isAdmin]
	);

	/**
	 * Atualiza uma mensagem (admin)
	 */
	const updateMessage = useCallback(
		async (messageId: number, data: UpdateMessagePayload) => {
			if (!isAdmin) throw new Error("Apenas administradores");
			const result = await updateMessageMutation.mutateAsync({ id: messageId, payload: data });
			return result.data;
		},
		[updateMessageMutation, isAdmin]
	);

	/**
	 * Exclui uma mensagem (admin)
	 */
	const deleteMessage = useCallback(
		async (messageId: number) => {
			if (!isAdmin) throw new Error("Apenas administradores");
			await deleteMessageMutation.mutateAsync(messageId);
		},
		[deleteMessageMutation, isAdmin]
	);

	// Compatibilidade com cÃ³digo antigo
	const loadUnreadCount = useCallback(async () => {
		await refetchCount();
	}, [refetchCount]);

	return {
		messages,
		unreadCount: unreadCountData?.unread_count || 0,
		loading: isMessagesLoading || isCountLoading,
		error: messagesError,
		refresh,
		markAsRead,
		markMultipleAsRead,
		createMessage,
		updateMessage,
		deleteMessage,
		loadUnreadCount,
		isAdmin,
		setMessages: () => console.warn("setMessages deprecated - state managed by React Query"),
		setUnreadCount: () => console.warn("setUnreadCount deprecated - state managed by React Query"),
	};
}
